// Browse by size (diameter / Ø) for vintage and Murano lamps.
//
// Shopify products at Farveladeland are not tagged with structured size data,
// so we do two things:
//   1. Use the Storefront API search to narrow to lamp-related products that
//      mention a diameter (Ø, diameter, ø) in title or description.
//   2. After fetching, parse the diameter out of each product's description
//      and bucket it. Anything we cannot parse is dropped from the size view.
//
// Coverage will not be 100% — only products where the description includes
// a parseable "Ø 24 cm" / "Diameter: 22 cm" / "ø: 18cm" — but it's the
// pragmatic way to ship this without re-tagging the entire catalog.

import type { ShopifyProduct } from "./shopify";
import { getEnrichedDiameter } from "./enrichment";

export type SizeBucket = {
  /** URL slug, /storrelse/<slug> */
  slug: string;
  labelDa: string;
  labelEn: string;
  /** Italian eyebrow label, mirrors the colour pages */
  italian: string;
  /** Tailwind background class defined in src/styles.css (panel-*) */
  panel: string;
  /** Inclusive lower bound, cm */
  min: number;
  /** Exclusive upper bound, cm. Use Infinity for "and up". */
  max: number;
  introDa: string;
  introEn: string;
};

// Bucket ranges are tuned to the actual catalogue: lamps cluster between
// 26 and 48 cm in diameter, so we use four descriptive bands instead of
// six numeric ones. Buckets that come back empty at fetch time are hidden
// from the UI (see /storrelse and /storrelse/$range), so it's fine to keep
// extra buckets here for future stock — just unhide them by stocking the
// products.
export const SIZE_BUCKETS: SizeBucket[] = [
  {
    slug: "kompakt",
    labelDa: "Kompakt 20–32 cm",
    labelEn: "Compact 20–32 cm",
    italian: "Compatto",
    panel: "panel-salvia",
    min: 20,
    max: 32,
    introDa:
      "Vores mindste Murano-lamper. Pæne på et natbord, en konsol eller som væglampe i en smal entré.",
    introEn:
      "Our smallest Murano lamps. Lovely on a bedside table, a console, or as a wall light in a narrow hallway.",
  },

  {
    slug: "klassisk",
    labelDa: "Klassisk 32–38 cm",
    labelEn: "Classic 32–38 cm",
    italian: "Classico",
    panel: "panel-burro",
    min: 32,
    max: 38,
    introDa:
      "Den hverdagsstørrelse vi sælger flest af — passer som loftlampe i soveværelset, entréen eller på badeværelset.",
    introEn:
      "The everyday size we sell most of — perfect as a ceiling lamp in the bedroom, the hallway, or the bathroom.",
  },
  {
    slug: "stor",
    labelDa: "Stor 38–44 cm",
    labelEn: "Large 38–44 cm",
    italian: "Grande",
    panel: "panel-pesca",
    min: 38,
    max: 44,
    introDa:
      "Markante pendler og plafonder. Godt over et spisebord til fire-seks personer eller som hovedlys i stuen.",
    introEn:
      "Statement pendants and ceiling lamps. Good above a dining table for four to six, or as the main light in a living room.",
  },
  {
    slug: "imponerende",
    labelDa: "Imponerende 44 cm+",
    labelEn: "Imposing 44 cm+",
    italian: "Imponente",
    panel: "panel-cielo",
    min: 44,
    max: Infinity,
    introDa:
      "De store fund — lysekroner, mega peacocks og oversize pendler. Bedst i rum med højt til loftet.",
    introEn:
      "The big finds — chandeliers, mega peacocks and oversize pendants. Best in rooms with high ceilings.",
  },
];

export function getSizeBucketBySlug(slug: string): SizeBucket | undefined {
  return SIZE_BUCKETS.find((s) => s.slug === slug);
}

/**
 * Parse a diameter (cm) from a Danish/English product description.
 *
 * Description matches things like:
 *   Ø 24 cm / ø: 22cm / ø 22,5 cm / ø24cm
 *   Diameter 30 cm / Diameter: 18 cm / Diameter ca. 22 cm
 *   Bredde 24 cm / Bredde: 24cm  (Farveladeland house convention)
 *
 * Heights ("H 30 cm" / "Højde 30") are intentionally ignored in the
 * description — those are not the diameter.
 *
 * Returns the diameter in cm as a number, or null if nothing parseable.
 */
function parseFromDescription(description: string | undefined | null): number | null {
  if (!description) return null;
  const text = description.replace(/[·•|]/g, " ").toLowerCase();

  // Patterns ordered most-specific → least. Each captures a number in cm.
  const num = String.raw`(\d{1,3}(?:[.,]\d{1,2})?)`;
  const ca = String.raw`(?:ca\.?|approx\.?|~)?`;
  const patterns: RegExp[] = [
    new RegExp(String.raw`\bdiameter\b\s*[:=]?\s*${ca}\s*${num}\s*cm\b`),
    new RegExp(String.raw`\bbredde\b\s*[:=]?\s*${ca}\s*${num}\s*cm\b`),
    new RegExp(String.raw`ø\s*[:=]?\s*${ca}\s*${num}\s*cm\b`),
    new RegExp(String.raw`ø\s*${num}\s*cm\b`),
  ];

  for (const re of patterns) {
    const m = text.match(re);
    if (m) {
      const n = parseFloat(m[1].replace(",", "."));
      if (!isNaN(n) && n > 0 && n < 500) return n;
    }
  }

  // Fallback: no Ø/diameter/bredde marker — scan all unmarked "N cm",
  // skip height/depth markers, and only keep values in a plausible lamp
  // diameter range. If exactly one candidate remains, trust it (handles
  // cases like "Minipendel 25 cm" in body text).
  const re = /(\d{1,3}(?:[.,]\d{1,2})?)\s*cm\b/g;
  const matches: number[] = [];
  let mm: RegExpExecArray | null;
  while ((mm = re.exec(text)) !== null) {
    const before = text.slice(Math.max(0, mm.index - 12), mm.index).trim();
    if (/(?:^|\s)(h|h\.|højde|hojde|height|dybde|depth|d|d\.)\s*[:=]?\s*$/.test(before)) continue;
    const n = parseFloat(mm[1].replace(",", "."));
    if (!isNaN(n) && n >= 10 && n <= 90) matches.push(n);
  }
  if (matches.length === 1) return matches[0];
  return null;
}

// Words that mean the product is definitely NOT a lamp. The Storefront
// search query is permissive (OR-joined), so vases, plates, glasses and
// ceramics occasionally slip through. We exclude them by title keyword.
const NON_LAMP_TERMS = [
  "tallerken", "fad", "skål", "skaal", "kop", "krus", "bakke", "vase",
  "krukke", "kande", "kandelaber", "glas", "vinglas", "champagneglas",
  "ølglas", "drikkeglas", "underkop", "saebeskal", "sæbeskal", "fisketallerken",
  "keramik", "fajance", "salatskål", "salatskaal", "smørrebrødsfad",
  "askebæger", "askebaeger", "lysestage", "ledning", "fatning", "skærm",
];

function isLamp(title: string | undefined | null): boolean {
  if (!title) return true; // be permissive when title missing
  const t = title.toLowerCase();
  // Must NOT contain a non-lamp term
  for (const term of NON_LAMP_TERMS) {
    if (t.includes(term)) return false;
  }
  return true;
}


/**
 * Parse a diameter from a product title. Per Farveladeland convention, any
 * "<number> cm" appearing in the title is the width/diameter — e.g.
 *   "Murano mushroom lampe 24 cm"
 *   "Vintage pendel 32cm hvid"
 *   "Lysekrone Ø 40 cm"
 */
function parseFromTitle(title: string | undefined | null): number | null {
  if (!title) return null;
  const text = title.toLowerCase();

  // 1. If the title explicitly marks a diameter (Ø / diameter / bredde),
  //    trust that number — it's the width by definition. This avoids picking
  //    up a height value that happens to be larger, e.g. "Ø 28 cm, H 35 cm"
  //    must bucket as 28, not 35.
  const num = String.raw`(\d{1,3}(?:[.,]\d{1,2})?)`;
  const markedPatterns: RegExp[] = [
    new RegExp(String.raw`ø\s*[:=]?\s*${num}\s*cm\b`),
    new RegExp(String.raw`\bdiameter\b\s*[:=]?\s*${num}\s*cm\b`),
    new RegExp(String.raw`\bbredde\b\s*[:=]?\s*${num}\s*cm\b`),
  ];
  for (const re of markedPatterns) {
    const m = text.match(re);
    if (m) {
      const n = parseFloat(m[1].replace(",", "."));
      if (!isNaN(n) && n > 0 && n < 500) return n;
    }
  }

  // 2. Fallback: pick the largest unmarked "N cm", skipping height markers.
  const re = /(\d{1,3}(?:[.,]\d{1,2})?)\s*cm\b/g;
  const matches: number[] = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const before = text.slice(Math.max(0, m.index - 8), m.index).trim();
    if (/(?:^|\s)(h|h\.|højde|hojde|height)\s*[:=]?\s*$/.test(before)) continue;
    const n = parseFloat(m[1].replace(",", "."));
    if (!isNaN(n) && n > 0 && n < 500) matches.push(n);
  }
  if (matches.length === 0) return null;
  return Math.max(...matches);
}


/**
 * Mushroom lamps are measured by HEIGHT, not diameter — that's the
 * meaningful "size" dimension for a table lamp. Detect by title keyword.
 */
function isMushroom(title: string | undefined | null): boolean {
  if (!title) return false;
  return /mushroom/i.test(title);
}

/**
 * Parse a height (cm) from a product's title or description. Looks for
 * explicit H / Højde / Height markers, e.g.
 *   "H 32 cm" / "h: 32cm" / "Højde 28 cm" / "height 30cm"
 * Returns the height in cm as a number, or null.
 */
function parseHeightCm(input: {
  title?: string | null;
  description?: string | null;
}): number | null {
  const num = String.raw`(\d{1,3}(?:[.,]\d{1,2})?)`;
  const re = new RegExp(
    String.raw`\b(?:h|h\.|højde|hojde|height)\s*[:=]?\s*(?:ca\.?|approx\.?|~)?\s*${num}\s*cm\b`,
    "i",
  );
  for (const src of [input.title, input.description]) {
    if (!src) continue;
    const m = src.replace(/[·•|]/g, " ").match(re);
    if (m) {
      const n = parseFloat(m[1].replace(",", "."));
      if (!isNaN(n) && n > 0 && n < 200) return n;
    }
  }
  return null;
}

/**
 * Public: parse diameter from either a raw description string (legacy) or a
 * product node with both title and description. Title takes precedence when
 * present, since the house convention is that any "N cm" in the title IS
 * the width.
 */
export function parseDiameterCm(
  input:
    | string
    | undefined
    | null
    | { title?: string | null; description?: string | null; handle?: string | null }
): number | null {
  if (input == null) return null;
  if (typeof input === "string") return parseFromDescription(input);
  // Deterministic parsing first (title → description), then fall back to the
  // AI-enrichment cache for products where neither mentions a diameter.
  return (
    parseFromTitle(input.title) ??
    parseFromDescription(input.description) ??
    getEnrichedDiameter(input.handle ?? null)
  );
}

/**
 * Return the size value (cm) used to bucket a product. For mushroom lamps
 * this is the height; for everything else it's the diameter.
 */
export function getSizeValueCm(input: {
  title?: string | null;
  description?: string | null;
  handle?: string | null;
}): number | null {
  if (isMushroom(input.title)) {
    return parseHeightCm(input) ?? parseDiameterCm(input);
  }
  return parseDiameterCm(input);
}

/**
 * Group products into the diameter buckets defined in SIZE_BUCKETS.
 * Products without a parseable size are excluded.
 */
export function bucketize(products: ShopifyProduct[]): Map<string, ShopifyProduct[]> {
  const out = new Map<string, ShopifyProduct[]>();
  for (const b of SIZE_BUCKETS) out.set(b.slug, []);

  for (const p of products) {
    if (!isLamp(p.node.title)) continue;
    const d = getSizeValueCm({
      title: p.node.title,
      description: p.node.description,
      handle: p.node.handle,
    });
    if (d == null) continue;
    const bucket = SIZE_BUCKETS.find((b) => d >= b.min && d < b.max);
    if (bucket) out.get(bucket.slug)!.push(p);
  }

  return out;
}

/**
 * Filter a fetched product list down to one bucket.
 */
export function filterByBucket(
  products: ShopifyProduct[],
  bucket: SizeBucket
): ShopifyProduct[] {
  return products.filter((p) => {
    if (!isLamp(p.node.title)) return false;
    const d = getSizeValueCm({
      title: p.node.title,
      description: p.node.description,
      handle: p.node.handle,
    });
    return d != null && d >= bucket.min && d < bucket.max;
  });

}


/**
 * Build a Storefront search query that narrows to lamp-ish products.
 * We intentionally do NOT require a measurement marker — Shopify's search
 * tokeniser doesn't reliably match "30cm" (no space) or the Ø character,
 * so we fetch all lamp products and let parseDiameterCm filter them.
 */
export const SIZE_SEARCH_QUERY =
  "lampe OR lamp OR pendel OR plafond OR lysekrone OR chandelier OR mushroom OR loftlampe OR væglampe OR vaeglampe OR bordlampe";

